/**
 * quizExitUrl — WEB-2164 (решение владельца 17.09, вариант «В»).
 *
 * ОДНА сборка адреса СЕРВЕРНОЙ ТОЧКИ УХОДА на внешнюю оплату. Уводящих
 * концовок у квиза две — правило логики «увести по адресу» и блок «редирект» по
 * обратному отсчёту, — и адрес у них обязан быть собран ОДИНАКОВО: вторая копия
 * сборки и есть то, из чего вырастает расхождение (шрам WEB-1687).
 *
 * ПОЧЕМУ УХОД ВООБЩЕ ИДЁТ ЧЕРЕЗ СЕРВЕР. Шаг воронки «ушёл на оплату» —
 * единственный переход связки клиента, который совершаем МЫ. Браузерным
 * событием его мерить нельзя: оно теряется на медленной сети и при быстром
 * уходе, а на этом показателе планируется считать счёт (решение «Б1» зонта
 * WEB-2231) — подавил значит недоплатил.
 *
 * ЧЕГО В ЗАПРОСЕ НЕТ: АДРЕСА НАЗНАЧЕНИЯ. Едут только идентификаторы цели, адрес
 * сервер достаёт сам из опубликованного содержимого (`exit-target.util.ts`).
 * Иначе наш домен становится универсальным переадресатором «куда угодно», такими
 * ссылками пользуются для обмана, и страдает репутация домена, с которого идёт
 * реклама (п.1 требований WEB-2164, та же тревога в WEB-2129 п.3).
 *
 * `null` — «точку собрать не из чего»: нет проекта/слага (превью черновика,
 * канва конструктора) либо цель не одна. Вызывающий отвечает на `null` ЗАПАСНЫМ
 * путём — прямым переходом по уже очищенному адресу: человек обязан дойти до
 * оплаты, даже когда посчитать переход мы не смогли (п.6 требований — «деньги и
 * аналитика не важнее того, что человек должен дойти»).
 */
import { API_BASE_URL } from '@/config/api-url';
import { resolvePlayerConsent } from '@/utils/analytics/playerPixelEvents';
import { getVisitorIdRead } from '@/utils/analytics/visitorId';

/** Решение посетителя о согласии в запросе ухода (WEB-3296). */
export type ExitConsent = 'granted' | 'denied' | 'unknown';

function toExitConsent(value: string | null | undefined): ExitConsent {
  return value === 'granted' || value === 'denied' ? value : 'unknown';
}

/**
 * WEB-3296 — поля посетителя для точки ухода по решению плеера о согласии.
 *
 * Без согласия идентификатор не берётся вовсе (`getVisitorId` его бы завёл):
 * отказ и «решения нет» едут только словом `consent`, ключом человека сервер
 * возьмёт вкладку `s`. При согласии — идентификатор и признак «прочитан с
 * устройства» (`getVisitorIdRead`). Одна функция на плеер квиза и пейволла.
 */
export function resolveExitVisitorFields(): {
  consent: ExitConsent;
  visitorId?: string;
  visitorFromDevice: boolean;
} {
  const consent = toExitConsent(resolvePlayerConsent());
  if (consent !== 'granted') return { consent, visitorFromDevice: false };
  const read = getVisitorIdRead();
  return { consent, visitorId: read.id, visitorFromDevice: read.fromDevice };
}

/** Поля строки запроса, общие у точек ухода квиза и пейволла. */
type ExitUrlFields = Omit<QuizExitUrlParams, 'quizSlug'>;

export interface PaywallExitUrlParams extends ExitUrlFields {
  paywallSlug: string;
}

export interface QuizExitUrlParams {
  projectId: string;
  quizSlug: string;
  /**
   * WEB-3296: решение посетителя о согласии. `v` уезжает только при
   * `granted`; не передано — `v` не едет и поля `consent` в запросе нет.
   */
  consent?: ExitConsent | null;
  /** WEB-3296: `v` прочитан с устройства (`getVisitorIdRead`) → `vp=1`. */
  visitorFromDevice?: boolean | null;
  /** WEB-3296: идентификатор сессии оплаты Stripe (`session_id`) → `cs`. */
  checkoutSessionId?: string | null;
  /** Идентификатор правила логики с действием REDIRECT_URL. */
  ruleId?: string | null;
  /** Идентификатор блока «редирект» или кнопки-ссылки (WEB-3296). */
  blockId?: string | null;
  /** Экран, с которого ушли. */
  screenId?: string | null;
  /** Идентификатор посещения (сессия плеера). */
  sessionId?: string | null;
  /**
   * Идентификатор посетителя (хранилище страницы) — КЛЮЧ НАШЕГО УЧЁТА.
   *
   * Проход по швам пачки 45 (С3): наружу, в чужой адрес, он НЕ идёт. Для
   * подстановки `{visitor_id}` есть отдельный `macroVisitorId` — собственный
   * сбор и передача третьему лицу это разные вещи и разрешаются по-разному.
   */
  visitorId?: string | null;
  /**
   * Значение метки `{visitor_id}` ДЛЯ ЧУЖОГО АДРЕСА.
   *
   * Плеер заполняет его только когда передача третьим лицам не запрещена
   * (решение баннера и сигнал браузера Global Privacy Control — один арбитр
   * `resolveSharingConsent`). Пусто → сервер подставлять нечего, и постоянный
   * идентификатор человека не уезжает ни в журналы чужого сервера, ни в
   * историю браузера.
   */
  macroVisitorId?: string | null;
  /** Номер шага, С ЕДИНИЦЫ — контракт плеера (WEB-1153). */
  screenIndex?: number | null;
  /** Всего шагов на пути посетителя. */
  screenTotal?: number | null;
  /* ---------------------------------------------------------------------- *
   * WEB-2123 — ЗНАЧЕНИЯ МЕТОК ДЛЯ ПОДСТАНОВКИ В ИСХОДЯЩИЙ АДРЕС.
   *
   * Почему их везёт браузер. Метки кампании и рекламного клика лежат в
   * хранилище плеера на НАШЕМ хосте (куки `qb_utm_lt`, `_aff_click_id`), а
   * точка ухода — отдельный запрос, в адресе которого рекламных меток уже нет.
   * Не привези их плеер — подставлять серверу нечего, и вся фича мертва.
   *
   * Почему это не открывает переадресатор: адресом назначения ни одно из этих
   * значений не становится (адрес сервер достаёт сам по идентификатору цели),
   * они уходят только в ЗНАЧЕНИЯ параметров чужого адреса и только
   * закодированными (`shared/utils/outbound-url-macros.ts`).
   * ---------------------------------------------------------------------- */
  /** Метки кампании последнего касания (форма `getStoredUtm`). */
  utm?: {
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    utmTerm?: string;
    utmContent?: string;
  } | null;
  /** Идентификатор рекламного клика (`_aff_click_id`). */
  clickId?: string | null;
  /**
   * Ответы, которые автор ЯВНО разрешил передавать: «вопрос → вариант(ы)».
   * Значение — то, что лежит в форме плеера: у вопроса с несколькими ответами
   * это идентификаторы вариантов через запятую.
   */
  answers?: Record<string, string> | null;
}

function nonEmpty(value: string | null | undefined): string {
  return typeof value === 'string' ? value : '';
}

/**
 * Форма идентификатора варианта — ТА ЖЕ, что у сервера
 * (`quizzes/utils/exit-target.util.ts`, `ANSWER_ID_RE`). Копия здесь не ради
 * удобства: всё, что на идентификатор не похоже, — это чей-то свободный текст
 * (почта, «хочу похудеть»), и уезжать наружу ему нельзя. Сервер отбросит такую
 * пару тоже, но отправлять её и надеяться на чужую проверку — ровно тот класс,
 * из которого растут утечки.
 */
const ANSWER_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;

/** Не больше стольких пар — тот же предел, что на сервере. */
export const MAX_ANSWER_PAIRS = 20;

/**
 * Не больше стольких ЗНАКОВ в строке `a` — тот же предел, что на сервере
 * (`backend/src/quizzes/utils/exit-target.util.ts`, `MAX_ANSWERS_PARAM_LENGTH`).
 *
 * ЗАЧЕМ ВТОРОЙ ПРЕДЕЛ. Пределов у строки два, и они про разное: сколько ПАР и
 * сколько ЗНАКОВ. До прохода 3 свежего взгляда пачки 45 плеер знал только
 * первый, а сервер — только второй (1024), и числа не совпадали: двадцать пар
 * «uuid:uuid» — это 1479 знаков, и глобальный пайп отбивал переход 400-м ДО
 * обработчика. Это денежный путь: вместо страницы оплаты покупатель получал
 * тело ошибки.
 *
 * ПОЧЕМУ НАБИРАЕМ, А НЕ РЕЖЕМ. Строка режется по ПАРАМ: обрезанная посередине
 * пара приехала бы полупарой, сервер отбросил бы её целиком
 * (`parseOutboundAnswers`), и наружу молча уехало бы меньше ответов, чем
 * обещано автору. Поэтому пара добавляется, только если целиком укладывается.
 *
 * Совпадение обоих чисел на обоих концах держит сторож
 * `backend/src/shared/guards/exit-answers-limits-ends.web2164.spec.ts`.
 */
export const MAX_ANSWERS_PARAM_LENGTH =
  MAX_ANSWER_PAIRS * (64 + 1 + 64) + (MAX_ANSWER_PAIRS - 1);

/**
 * Разделитель НЕСКОЛЬКИХ вариантов одного вопроса.
 *
 * Запятая уже занята: ею разделяются ПАРЫ. Отправь мы «q1:o1,o2» — сервер
 * разобрал бы «o2» как пару без двоеточия и молча выбросил, то есть у вопроса
 * с несколькими ответами наружу уезжал бы только первый выбор. Тильда в
 * идентификаторы не входит (см. `ANSWER_ID_RE`), поэтому разбор однозначен.
 */
const ANSWER_MULTI_SEPARATOR = '~';

/** `q1:o1,q2:o7~o8` — пары «вопрос:вариант(ы)»; негодное отбрасывается. */
function buildAnswersParam(answers: Record<string, string>): string {
  const pairs: string[] = [];
  // Длина уже набранного, с учётом запятых-разделителей.
  let length = 0;
  for (const [questionId, raw] of Object.entries(answers)) {
    if (pairs.length >= MAX_ANSWER_PAIRS) break;
    if (!ANSWER_ID_RE.test(questionId)) continue;
    const optionIds = String(raw ?? '')
      .split(',')
      .map((part) => part.trim())
      .filter((part) => ANSWER_ID_RE.test(part));
    if (optionIds.length === 0) continue;
    const pair = `${questionId}:${optionIds.join(ANSWER_MULTI_SEPARATOR)}`;
    // Пара добавляется ЦЕЛИКОМ либо не добавляется вовсе (см. докблок
    // `MAX_ANSWERS_PARAM_LENGTH`): обрезок сервер отбросил бы молча.
    const next = length + (pairs.length === 0 ? 0 : 1) + pair.length;
    if (next > MAX_ANSWERS_PARAM_LENGTH) break;
    pairs.push(pair);
    length = next;
  }
  return pairs.join(',');
}

export function buildQuizExitUrl(params: QuizExitUrlParams): string | null {
  return buildExitUrl('quiz', params.quizSlug, params);
}

/**
 * WEB-3296 (С-91) — точка ухода ПЕЙВОЛЛА (`/public/paywall/by-id/…/exit`,
 * завела WEB-3295). Поля строки запроса те же, что у квиза, и собирает их та
 * же дверь — второй копии правила «`v` только при согласии» не заводится.
 */
export function buildPaywallExitUrl(
  params: PaywallExitUrlParams,
): string | null {
  return buildExitUrl('paywall', params.paywallSlug, params);
}

function buildExitUrl(
  kind: 'quiz' | 'paywall',
  slugRaw: string,
  params: ExitUrlFields,
): string | null {
  const projectId = nonEmpty(params.projectId);
  const quizSlug = nonEmpty(slugRaw);
  if (projectId === '' || quizSlug === '') return null;

  const ruleId = nonEmpty(params.ruleId);
  const blockId = nonEmpty(params.blockId);
  // Ровно ОДНА цель — та же граница, что у сервера (`resolveExitTarget`): и
  // «ни одной», и «обе сразу» там дают `null`, и собирать запрос, на который
  // заведомо ответят отказом, незачем — человек просто не ушёл бы.
  if ((ruleId === '') === (blockId === '')) return null;

  const query = new URLSearchParams();
  if (ruleId !== '') query.set('rule', ruleId);
  else query.set('block', blockId);

  const screenId = nonEmpty(params.screenId);
  if (screenId !== '') query.set('screen', screenId);
  const sessionId = nonEmpty(params.sessionId);
  if (sessionId !== '') query.set('s', sessionId);
  // WEB-3296: ключ человека в денежной записи — `v`, но только при `vp=1` и
  // `consent=granted` (WEB-3295). Без согласия `v` не едет ВОВСЕ: без него
  // идентификатор живёт в памяти страницы, и каждая перезагрузка в ЕС стала
  // бы новым платным переходом. Решения не передали — это «нет согласия».
  const consent = toExitConsent(params.consent);
  const visitorId = consent === 'granted' ? nonEmpty(params.visitorId) : '';
  if (visitorId !== '') {
    query.set('v', visitorId);
    if (params.visitorFromDevice === true) query.set('vp', '1');
  }
  if (params.consent != null) query.set('consent', consent);
  if (typeof params.screenIndex === 'number' && Number.isFinite(params.screenIndex)) {
    query.set('si', String(params.screenIndex));
  }
  if (typeof params.screenTotal === 'number' && Number.isFinite(params.screenTotal)) {
    query.set('st', String(params.screenTotal));
  }

  // WEB-2123: значения меток. Имена параметров совпадают с именами меток буква
  // в букву — лишнее сопоставление «параметр → метка» это лишнее место, где
  // однажды перепутают две строки (та же причина, что в DTO на сервере).
  const utm = params.utm ?? {};
  const utmByMacro: ReadonlyArray<[string, string | undefined]> = [
    ['utm_source', utm.utmSource],
    ['utm_medium', utm.utmMedium],
    ['utm_campaign', utm.utmCampaign],
    ['utm_term', utm.utmTerm],
    ['utm_content', utm.utmContent],
  ];
  for (const [name, value] of utmByMacro) {
    const clean = nonEmpty(value);
    if (clean !== '') query.set(name, clean);
  }
  const clickId = nonEmpty(params.clickId);
  if (clickId !== '') query.set('click_id', clickId);
  // С3: значение `{visitor_id}` едет СВОИМ параметром рядом с остальными
  // значениями меток, а не переиспользует ключ учёта `v`.
  const macroVisitorId = nonEmpty(params.macroVisitorId);
  if (macroVisitorId !== '') query.set('mv', macroVisitorId);
  const answers = buildAnswersParam(params.answers ?? {});
  if (answers !== '') query.set('a', answers);
  // WEB-3296 (29-6): идентификатор сессии оплаты — сверяет сервер, слову
  // плеера «я после оплаты» не верим.
  const checkoutSessionId = nonEmpty(params.checkoutSessionId);
  if (checkoutSessionId !== '') query.set('cs', checkoutSessionId);

  const path = `/public/${kind}/by-id/${encodeURIComponent(projectId)}/${encodeURIComponent(quizSlug)}/exit`;
  return `${API_BASE_URL}${path}?${query.toString()}`;
}
